import { Edit, useForm, useSelect } from "@refinedev/antd";
import { HttpError } from "@refinedev/core";
import {
  Form,
  Select,
  Radio,
  DatePicker,
  Switch,
  Row,
  Col,
  Card,
  App,
  Input,
} from "antd";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useNavigate } from "react-router";
import type { IncidenceEditFormValues, IncidenceDetail } from "../../types";
import dayjs from "dayjs";
import { usePageTitle } from "../../hooks";

const { TextArea } = Input;

/**
 * IncidenceEdit Component
 * Form for editing an existing incidence
 * Only allows editing if status is "Abierta" (Open)
 * Immutable fields: kind_incidence (type)
 */
export const IncidenceEdit = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { formProps, form, onFinish, query } = useForm<
    IncidenceDetail,
    HttpError,
    IncidenceEditFormValues
  >({
    action: "edit",
    // Repointed to the unified Ticket model. The serializer keeps
    // the legacy aliases (`num_incidence`, `status_incidence`,
    // `kind_incidence_detail`, …) so the page renders untouched.
    resource: "v1/tickets",
    redirect: false,
    onMutationSuccess: (data) => {
      message.success(
        t("incidence.edit_success", "Incidencia actualizada exitosamente")
      );
      // Redirect to the detail page
      navigate(`/incidences/${data.data.id}`);
    },
    onMutationError: (error) => {
      message.error(
        error?.message ||
          t("incidence.edit_error", "Error al actualizar la incidencia")
      );
    },
  });

  const incidenceData = query?.data;
  const isLoading = query?.isLoading ?? false;
  const record = incidenceData?.data;
  
  // Update page title with incidence number
  const pageTitle = record 
    ? `Editar Incidencia ${record.num_incidence}`
    : "Editar Incidencia";
  usePageTitle(pageTitle);

  // Check if incidence can be edited (only if status is "Abierta")
  const canEdit = record?.status_incidence === 1;

  // Load users for assigned_to select dropdown
  const { selectProps: userSelectProps } = useSelect({
    resource: "app-users",
    optionLabel: "name",
    optionValue: "id",
    pagination: {
      pageSize: 100,
    },
    defaultValue: record?.assigned_to_detail?.id,
  });

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: IncidenceEditFormValues) => {
    if (!canEdit) {
      message.error(
        t(
          "incidence.cannot_edit_closed",
          "No se puede editar una incidencia cerrada o cancelada"
        )
      );
      return;
    }

    try {
      setIsSubmitting(true);

      // Format deadline if provided
      const data: any = {
        ...values,
        deadline: values.deadline
          ? dayjs(values.deadline).format("YYYY-MM-DD")
          : null,
      };

      await onFinish(data);
    } catch (error: any) {
      // Error handled by mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show warning if incidence cannot be edited
  if (!isLoading && !canEdit) {
    const titleText = record?.audit_detail?.producer?.name && record?.audit_detail?.reference
      ? `${t("incidence.edit", "Editar Incidencia")} - ${record.audit_detail.producer.name} (${record.audit_detail.reference})`
      : t("incidence.edit", "Editar Incidencia");

    return (
      <Edit
        isLoading={isLoading}
        title={titleText}
        headerButtons={<></>}
        footerButtons={<></>}
      >
        <Card>
          <p style={{ color: "red", fontSize: "16px" }}>
            {t(
              "incidence.cannot_edit_closed",
              "No se puede editar una incidencia cerrada o cancelada"
            )}
          </p>
        </Card>
      </Edit>
    );
  }

  const titleText = record?.audit_detail?.producer?.name && record?.audit_detail?.reference
    ? `${t("incidence.edit", "Editar Incidencia")} - ${record.audit_detail.producer.name} (${record.audit_detail.reference})`
    : t("incidence.edit", "Editar Incidencia");

  return (
    <Edit
      isLoading={isLoading}
      title={titleText}
      headerButtons={<></>}
      footerButtons={<></>}
      saveButtonProps={{
        loading: isSubmitting,
        disabled: !canEdit,
        onClick: () => {
          form.submit();
        },
      }}
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          description: record?.description,
          priority: record?.priority || "medium",
          assigned_to: record?.assigned_to_detail?.id,
          deadline: record?.deadline ? dayjs(record.deadline) : null,
          requires_immediate_action: record?.requires_immediate_action || false,
        }}
      >
        <Card
          title={t("incidence.basicInfo", "Información Básica")}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("incidence.number", "Número de Incidencia")}
              >
                <Input value={record?.num_incidence} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("incidence.type", "Tipo de Incidencia")}
              >
                <Input
                  value={record?.kind_incidence_detail?.name}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label={t("incidence.description", "Descripción")}
                name="description"
                rules={[
                  {
                    required: true,
                    message: t(
                      "incidence.description_required",
                      "La descripción es requerida"
                    ),
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder={t(
                    "incidence.description_placeholder",
                    "Describa la incidencia detectada..."
                  )}
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title={t("incidence.priority_and_assignment", "Prioridad y Asignación")}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label={t("incidence.priority_label", "Prioridad")}
                name="priority"
                rules={[
                  {
                    required: true,
                    message: t(
                      "incidence.priority_required",
                      "La prioridad es requerida"
                    ),
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value="low">
                    {t("incidence.priority.low", "Baja")}
                  </Radio>
                  <Radio value="medium">
                    {t("incidence.priority.medium", "Media")}
                  </Radio>
                  <Radio value="high">
                    {t("incidence.priority.high", "Alta")}
                  </Radio>
                  <Radio value="critical">
                    {t("incidence.priority.critical", "Crítica")}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("incidence.assigned_to", "Asignar a")}
                name="assigned_to"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <Select
                  {...userSelectProps}
                  placeholder={t(
                    "incidence.assigned_to_placeholder",
                    "Seleccione un usuario (opcional)"
                  )}
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("incidence.deadline", "Fecha Límite")}
                name="deadline"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder={t(
                    "incidence.deadline_placeholder",
                    "Seleccione una fecha (opcional)"
                  )}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label={t(
                  "incidence.requires_immediate_action",
                  "Requiere Acción Inmediata"
                )}
                name="requires_immediate_action"
                valuePropName="checked"
                tooltip={t(
                  "incidence.requires_immediate_action_tooltip",
                  "Marque esta opción si la incidencia requiere atención urgente"
                )}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </Edit>
  );
};
